import { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, mediaUrl } from "@/lib/api";
import MediaUpload from "@/components/admin/MediaUpload";

export default function EditProperty() {
  const [p, setP] = useState(null);

  useEffect(() => { api.get("/property/admin").then((r) => setP(r.data)).catch(() => {}); }, []);

  const save = async () => {
    await api.put("/property", p);
    toast.success("House info saved");
  };

  if (!p) return null;
  const set = (k, v) => setP({ ...p, [k]: v });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="overline text-brand-terracotta">Edit</span>
          <h1 className="font-display text-4xl tracking-tight mt-1">House Info</h1>
        </div>
        <Button onClick={save} data-testid="save-property" className="rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors"><Save size={16} className="mr-2" />Save</Button>
      </div>

      <div className="space-y-6">
        <Card title="Address">
          <Field label="Full Property Address"><Input data-testid="field-address" value={p.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="123 Main St, City, State ZIP" /></Field>
        </Card>

        <Card title="Where to Park">
          {[["Car / Truck", "parking_car_images"], ["RV / Trailer", "parking_rv_images"]].map(([label, key]) => (
            <div key={key} className="space-y-2">
              <Label className="block text-sm">{label} photos</Label>
              <div className="flex flex-wrap gap-3">
                {(p[key] || []).map((src, i) => (
                  <div key={i} className="relative">
                    <img src={mediaUrl(src)} alt="" className="h-16 w-24 object-cover rounded-lg border border-black/10" />
                    <button onClick={() => set(key, (p[key] || []).filter((_, x) => x !== i))} data-testid={`park-${key}-remove-${i}`} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-white text-xs flex items-center justify-center">×</button>
                  </div>
                ))}
              </div>
              <MediaUpload testid={`park-${key}`} type="image" label="Add photo" onUploaded={(d) => set(key, [...(p[key] || []), d.url])} />
            </div>
          ))}
        </Card>

        <Card title="Wi-Fi">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Network Name"><Input data-testid="field-wifi-name" value={p.wifi_name} onChange={(e) => set("wifi_name", e.target.value)} /></Field>
            <Field label="Password"><Input data-testid="field-wifi-password" value={p.wifi_password} onChange={(e) => set("wifi_password", e.target.value)} /></Field>
          </div>
        </Card>

        <Card title="Check-In">
          <Field label="Time"><Input data-testid="field-checkin-time" value={p.checkin_time} onChange={(e) => set("checkin_time", e.target.value)} /></Field>
          <Field label="Instructions"><Textarea data-testid="field-checkin-instructions" rows={3} value={p.checkin_instructions} onChange={(e) => set("checkin_instructions", e.target.value)} /></Field>
        </Card>

        <Card title="Check-Out">
          <Field label="Time"><Input data-testid="field-checkout-time" value={p.checkout_time} onChange={(e) => set("checkout_time", e.target.value)} /></Field>
          <Field label="Instructions"><Textarea data-testid="field-checkout-instructions" rows={3} value={p.checkout_instructions} onChange={(e) => set("checkout_instructions", e.target.value)} /></Field>
        </Card>

        <Card title="House Rules">
          <ListEditor
            items={p.house_rules} testid="rule"
            onChange={(items) => set("house_rules", items)}
            render={(val, onChange, i) => <Input data-testid={`rule-${i}`} value={val} onChange={(e) => onChange(e.target.value)} placeholder="Add a rule…" />}
            blank=""
          />
        </Card>

        <Card title="Important Contacts">
          <ListEditor
            items={p.contacts} testid="contact"
            onChange={(items) => set("contacts", items)}
            render={(val, onChange, i) => (
              <div className="grid grid-cols-2 gap-2 flex-1">
                <Input data-testid={`contact-label-${i}`} value={val.label} onChange={(e) => onChange({ ...val, label: e.target.value })} placeholder="Label" />
                <Input data-testid={`contact-value-${i}`} value={val.value} onChange={(e) => onChange({ ...val, value: e.target.value })} placeholder="Number / info" />
              </div>
            )}
            blank={{ label: "", value: "" }}
          />
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm space-y-4">
      <h2 className="font-display text-xl tracking-tight">{title}</h2>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return <div><Label className="mb-1.5 block text-sm">{label}</Label>{children}</div>;
}

function ListEditor({ items, onChange, render, blank, testid }) {
  const update = (i, val) => { const c = [...items]; c[i] = val; onChange(c); };
  const remove = (i) => onChange(items.filter((_, x) => x !== i));
  const add = () => onChange([...items, typeof blank === "object" ? { ...blank } : blank]);
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          {render(it, (v) => update(i, v), i)}
          <button onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`} className="shrink-0 text-brand-forest/30 hover:text-destructive transition-colors"><Trash2 size={18} /></button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} data-testid={`${testid}-add`} className="rounded-full mt-1"><Plus size={15} className="mr-1" />Add</Button>
    </div>
  );
}
